import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { initializeGalleryCategories, removeObsoleteCategories } from '../../scripts/initializeFirestore';
import { motion } from 'framer-motion';

const InitializeDatabase: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [cleanupStatus, setCleanupStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [cleanupMessage, setCleanupMessage] = useState('');

  const handleInitialize = async () => {
    setStatus('loading');
    setMessage('Initializing database...');
    toast.info('Initializing database... ⏳');

    try {
      await initializeGalleryCategories();
      setStatus('success');
      setMessage('✅ Database initialized successfully! Gallery categories have been created.');
      toast.success('Database initialized successfully! 🎉');
    } catch (error) {
      setStatus('error');
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setMessage(`❌ Error: ${errorMsg}`);
      toast.error(`Initialization failed: ${errorMsg}`);
    }
  };

  const handleCleanup = async () => {
    setCleanupStatus('loading');
    setCleanupMessage('Removing obsolete categories...');
    toast.info('Removing obsolete categories... ⏳');

    try {
      await removeObsoleteCategories();
      setCleanupStatus('success');
      setCleanupMessage('✅ Obsolete categories removed successfully! (Reading Nook & Creative Corner)');
      toast.success('Obsolete categories removed! 🗑️');
    } catch (error) {
      setCleanupStatus('error');
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setCleanupMessage(`❌ Error: ${errorMsg}`);
      toast.error(`Cleanup failed: ${errorMsg}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-blue to-brand-purple flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md text-center"
      >
        <h1 className="text-3xl font-bold text-brand-dark mb-4">Initialize Database</h1>
        <p className="text-brand-blue mb-8">
          Click the button below to create the initial gallery categories in Firestore.
          This only needs to be done once.
        </p>

        <button
          onClick={handleInitialize}
          disabled={status === 'loading' || status === 'success'}
          className="bg-brand-pink text-white px-8 py-3 rounded-bubble font-bold hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mb-4 w-full"
        >
          {status === 'loading' ? 'Initializing...' : status === 'success' ? 'Initialized ✓' : 'Initialize Database'}
        </button>

        {message && (
          <div
            className={`p-4 rounded-bubble mb-4 ${
              status === 'success' ? 'bg-green-100 text-green-800' : status === 'error' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
            }`}
          >
            {message}
          </div>
        )}

        <hr className="my-6 border-brand-blue/20" />

        <p className="text-brand-blue mb-4 text-sm">
          Remove obsolete gallery categories (Reading Nook & Creative Corner)
        </p>

        <button
          onClick={handleCleanup}
          disabled={cleanupStatus === 'loading' || cleanupStatus === 'success'}
          className="bg-red-500 text-white px-8 py-3 rounded-bubble font-bold hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mb-4 w-full"
        >
          {cleanupStatus === 'loading' ? 'Removing...' : cleanupStatus === 'success' ? 'Removed ✓' : 'Remove Obsolete Categories'}
        </button>

        {cleanupMessage && (
          <div
            className={`p-4 rounded-bubble ${
              cleanupStatus === 'success' ? 'bg-green-100 text-green-800' : cleanupStatus === 'error' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
            }`}
          >
            {cleanupMessage}
          </div>
        )}

        <div className="mt-8">
          <a href="/" className="text-brand-blue hover:text-brand-pink transition-colors">
            ← Back to Home
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default InitializeDatabase;

